import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * `<PaTrackerContainer />` — SSE subscription + state machine container
 * per CPL Phase 5 Phase D IMPL composition guide pair-with-DEV5 §4.1 +
 * §4.3 (2026-05-14).
 *
 * Subscribes to a demo orchestrator run via `IDemoOrchestratorClient`
 * and drives the canonical `<PaTrackerView />` presentational primitive
 * (`widgets/pa-tracker-view.tsx` from PR #816). Composition responsibilities:
 *
 *   - Derive `currentPhaseIndex` (0–4) from the event stream — monotonic
 *     high-watermark per `<PaTrackerView />` 5-phase contract
 *     (provider-initiated → authz-decision → payer-adjudication →
 *     pa-tracker-update → audit-publication)
 *   - Derive `decisionState` (`pending | in-review | approved | denied`)
 *     from `pa.status_change` events
 *   - Derive `<FederationVisualization />` 8-step animation watermark
 *     and lift via optional `onAnimationStepChange` callback
 *   - Render the provider/payer/scenario run summary (`<dl>`) above the
 *     tracker
 *   - Render the terminal-state `[View clinical rationale]` CTA + the
 *     SSE connection-state banners (reconnecting / completed) below
 *
 * # Cleanup discipline
 *
 * Effect keyed on `(client, runId)`; cleanup sets `cancelled` flag and
 * calls `subscription.unsubscribe()` (which the client uses to close
 * `EventSource` / clear scripted-event timers). Late deliveries that
 * race the unmount are no-ops thanks to the `cancelled` guard.
 *
 * # History
 *
 * v1: introduced `<PaTrackerTimeline />` (PR #857 first commit) — that
 * duplicated `widgets/PaTrackerView` which already existed at origin/main
 * via PR #816. This refactor composes the canonical primitive instead;
 * `<PaTrackerTimeline />` deleted in the same change.
 */
import * as React from 'react';
import { useLocale } from '../../i18n/locale-context.js';
import { PaTrackerView, } from '../../widgets/pa-tracker-view.js';
/**
 * `PaTrackerStatus` from the orchestrator and `PaTrackerDecisionState`
 * from `widgets/pa-tracker-view.tsx` share the same union shape
 * (`pending | in-review | approved | denied`); kept as separate names
 * at the contract layer for register-distinction-discipline (orchestrator
 * event payload vs. view presentation prop).
 */
const STATUS_TO_DECISION = {
    pending: 'pending',
    'in-review': 'in-review',
    approved: 'approved',
    denied: 'denied',
};
/**
 * Map an event stream to the canonical 5-phase index of
 * `<PaTrackerView />` per its `currentPhaseIndex` contract:
 *
 *   0 — provider-initiated (Mode A widget invocation at provider portal)
 *   1 — authz-decision (substrate authority routing; substrate-routed phase
 *       OR `federation.query` event observed)
 *   2 — payer-adjudication (`authz.decision` event OR `payer-adjudicating`
 *       phase observed)
 *   3 — pa-tracker-update (terminal status observed: approved | denied)
 *   4 — audit-publication (`audit.signed` event OR `phase.completed`)
 *
 * Monotonic — never regresses on out-of-order events.
 */
function derivePhaseIndex(events) {
    let idx = 0;
    for (const ev of events) {
        if (ev.type === 'phase.completed')
            return 4;
        if (ev.type === 'audit.signed' && idx < 4)
            idx = 4;
        if ((ev.status === 'approved' || ev.status === 'denied') &&
            idx < 3) {
            idx = 3;
        }
        if ((ev.type === 'authz.decision' || ev.phase === 'payer-adjudicating') &&
            idx < 2) {
            idx = 2;
        }
        if ((ev.type === 'federation.query' || ev.phase === 'substrate-routed') &&
            idx < 1) {
            idx = 1;
        }
    }
    return idx;
}
/**
 * Map an event stream to the canonical 8-step Phase 5 §E federation
 * animation watermark per `<FederationVisualization />`
 * `FederationAnimationStep` + DD §2.3 + DP M1 Run-of-Show v0.5 §11.
 * Monotonic.
 */
function deriveAnimationStep(events) {
    let step = 0;
    for (const ev of events) {
        if (ev.type === 'phase.completed')
            return 7;
        if (ev.type === 'audit.signed' && step < 6)
            step = 6;
        if (ev.type === 'authz.decision' && step < 5)
            step = 5;
        if (ev.phase === 'substrate-routed' && step < 4)
            step = 4;
        if (ev.type === 'federation.query' && step < 3)
            step = 3;
        if (ev.type === 'pa.status_change' && step < 2)
            step = 2;
        if (ev.type === 'phase.started' && step < 1)
            step = 1;
    }
    return step;
}
export function PaTrackerContainer(props) {
    const { t } = useLocale();
    const [status, setStatus] = React.useState('pending');
    const [connectionState, setConnectionState] = React.useState('connecting');
    const [events, setEvents] = React.useState([]);
    const [summary, setSummary] = React.useState({});
    React.useEffect(() => {
        let cancelled = false;
        const subscription = props.client.subscribeRun(props.runId, {
            onEvent: (event) => {
                if (cancelled)
                    return;
                setEvents((prev) => [...prev, event]);
                if (event.type === 'pa.status_change' && event.status !== undefined) {
                    setStatus(event.status);
                }
                if (event.providerName !== undefined ||
                    event.payerName !== undefined ||
                    event.scenarioSummary !== undefined) {
                    // Spread prev first so already-populated fields persist; overlay
                    // any newly-defined fields from this event. `exactOptionalPropertyTypes`
                    // requires the conditional spread pattern — never assign
                    // `{ x: undefined }` to an `x?: T` slot.
                    setSummary((prev) => ({
                        ...prev,
                        ...(event.providerName !== undefined
                            ? { providerName: event.providerName }
                            : {}),
                        ...(event.payerName !== undefined
                            ? { payerName: event.payerName }
                            : {}),
                        ...(event.scenarioSummary !== undefined
                            ? { scenarioSummary: event.scenarioSummary }
                            : {}),
                    }));
                }
            },
            onError: () => {
                if (cancelled)
                    return;
                setConnectionState('reconnecting');
            },
            onOpen: () => {
                if (cancelled)
                    return;
                setConnectionState('connected');
            },
            onClose: () => {
                if (cancelled)
                    return;
                setConnectionState('completed');
            },
        });
        return () => {
            cancelled = true;
            subscription.unsubscribe();
        };
    }, [props.client, props.runId]);
    // Phase 5 §E federation animation step lift — derive watermark from
    // the event stream + notify parent when it advances. Monotonic.
    const onAnimationStepChange = props.onAnimationStepChange;
    const animationStep = React.useMemo(() => deriveAnimationStep(events), [events]);
    const previousStepRef = React.useRef(undefined);
    React.useEffect(() => {
        if (onAnimationStepChange === undefined)
            return;
        if (previousStepRef.current === animationStep)
            return;
        previousStepRef.current = animationStep;
        onAnimationStepChange(animationStep);
    }, [animationStep, onAnimationStepChange]);
    const currentPhaseIndex = React.useMemo(() => derivePhaseIndex(events), [events]);
    const decisionState = STATUS_TO_DECISION[status];
    const isTerminalStatus = status === 'approved' || status === 'denied';
    return (_jsxs("section", { className: "pa-tracker", "data-testid": "pa-tracker", "aria-labelledby": "pa-tracker-view-heading", children: [summary.providerName !== undefined ||
                summary.payerName !== undefined ||
                summary.scenarioSummary !== undefined ? (_jsxs("dl", { className: "pa-tracker__summary", "data-testid": "pa-tracker-summary", children: [summary.providerName !== undefined ? (_jsxs("div", { className: "pa-tracker__summary-row", children: [_jsx("dt", { children: t('account.prior_auth.summary.provider_label') }), _jsx("dd", { children: summary.providerName })] })) : null, summary.payerName !== undefined ? (_jsxs("div", { className: "pa-tracker__summary-row", children: [_jsx("dt", { children: t('account.prior_auth.summary.payer_label') }), _jsx("dd", { children: summary.payerName })] })) : null, summary.scenarioSummary !== undefined ? (_jsxs("div", { className: "pa-tracker__summary-row", children: [_jsx("dt", { children: t('account.prior_auth.summary.scenario_label') }), _jsx("dd", { children: summary.scenarioSummary })] })) : null] })) : null, _jsx(PaTrackerView, { currentPhaseIndex: currentPhaseIndex, decisionState: decisionState }), isTerminalStatus ? (_jsx("button", { type: "button", className: "pa-tracker__view-rationale-cta", onClick: props.onViewClinicalRationale, "data-testid": "pa-tracker-view-rationale-cta", children: t('account.prior_auth.tracker.view_clinical_rationale_cta') })) : null, connectionState === 'reconnecting' ? (_jsx("div", { role: "status", className: "pa-tracker__connection-warning", "data-testid": "pa-tracker-connection-warning", children: t('account.prior_auth.tracker.connection_lost') })) : null, connectionState === 'completed' ? (_jsx("div", { role: "status", className: "pa-tracker__connection-completed", "data-testid": "pa-tracker-connection-completed", children: t('account.prior_auth.tracker.run_complete') })) : null] }));
}
//# sourceMappingURL=pa-tracker-container.js.map